// NotificationDispatcher.jsx - Developer Notification Dispatcher Page
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Switch,
  Badge,
  Stack,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IconDeviceMobile,
  IconBrowser,
  IconDeviceMobileMessage,
  IconSend,
  IconSearch,
  IconUsers,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconBellRinging,
  IconFilter,
  IconRefresh,
  IconSparkles,
  IconDeviceAnalytics,
  IconMailForward,
  IconWorldUpload,
  IconInfoCircle,
  IconChecklist
} from '@tabler/icons-react';

import { dispatchNotification, getNotificationRecipients } from '../../services/notificationAPIs';
import { IMAGE_BASE_URL } from '../../config';

const PLATFORMS = [
  {
    id: 'MOBILE',
    title: 'Mobile Only (Android)',
    badge: 'Mobile Devices',
    icon: IconDeviceMobile,
    color: '#0EA5E9',
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
    bgLight: 'rgba(14, 165, 233, 0.08)',
    borderActive: '#0EA5E9',
    description: 'Sends strictly to Android mobile devices via device push & app notification. Bypasses web feed. Perfect for mobile app updates & mobile-specific notices.'
  },
  {
    id: 'WEB',
    title: 'Web Portal Only',
    badge: 'Web Browser',
    icon: IconBrowser,
    color: '#6366F1',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
    bgLight: 'rgba(99, 102, 241, 0.08)',
    borderActive: '#6366F1',
    description: 'Delivered only to the Web HRMS notification drawer. Will not trigger notifications or buzz mobile devices.'
  },
  {
    id: 'ALL',
    title: 'Omnichannel (Both)',
    badge: 'Web & Mobile',
    icon: IconWorldUpload,
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    bgLight: 'rgba(16, 185, 129, 0.08)',
    borderActive: '#10B981',
    description: 'Dispatches simultaneously to both Web portal and Android mobile devices for maximum employee reach.'
  }
];

const TEMPLATES = [
  {
    label: '📱 Mobile App Update',
    title: 'New Mobile App Update Available (v1.0.3)',
    message: 'A new version of AXO People is ready for installation on Android with performance improvements and bug fixes. Please update your mobile application.',
    platform: 'MOBILE',
    type: 'UPDATE'
  },
  {
    label: '⚠️ System Maintenance',
    title: 'Scheduled System Maintenance Notice',
    message: 'AXO People platform will undergo scheduled infrastructure maintenance on Sunday between 02:00 and 04:00 UTC. Brief disruptions may occur.',
    platform: 'ALL',
    type: 'WARNING'
  },
  {
    label: '🎉 Company Townhall',
    title: 'Quarterly Company Townhall Meeting',
    message: 'Join us this Thursday at 16:00 for the AXO Q3 Townhall session. We will cover company milestones, team achievements, and future roadmap.',
    platform: 'ALL',
    type: 'ANNOUNCEMENT'
  },
  {
    label: '📋 HR Handbook Update',
    title: 'Updated Company Policies & Guidelines',
    message: 'The HR handbook and remote-work guidelines have been updated in your web portal. Please review the new document at your convenience.',
    platform: 'WEB',
    type: 'INFO'
  }
];

const ROLE_COLORS = {
  DEV: { bg: '#FDF2F8', text: '#BE185D', border: '#FBCFE8' },
  MANAGER: { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
  HR: { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' },
  DIRECTOR: { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' },
  EMPLOYEE: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' }
};

export default function NotificationDispatcher() {
  const theme = useTheme();

  // State
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [platform, setPlatform] = useState('MOBILE');
  const [notificationType, setNotificationType] = useState('ANNOUNCEMENT');
  const [sendEmail, setSendEmail] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');

  // Preview Mode: 'MOBILE' or 'WEB'
  const [previewTab, setPreviewTab] = useState('MOBILE');

  // Actions & UI Feedback
  const [dispatching, setDispatching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [lastDispatched, setLastDispatched] = useState(null);

  // Fetch recipients
  const fetchRecipients = async () => {
    setLoadingEmployees(true);
    try {
      const data = await getNotificationRecipients();
      const list = Array.isArray(data) ? data : [];
      setEmployees(list);
    } catch (err) {
      console.error('Failed to load recipients:', err);
      setToast({
        open: true,
        message: 'Could not fetch employee list. Please check your backend connection.',
        severity: 'error'
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
  }, []);

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const q = searchQuery.toLowerCase().trim();
      const matchText =
        !q ||
        (emp.fullName && emp.fullName.toLowerCase().includes(q)) ||
        (emp.email && emp.email.toLowerCase().includes(q)) ||
        (emp.position && emp.position.toLowerCase().includes(q)) ||
        (emp.officeName && emp.officeName.toLowerCase().includes(q));

      const matchRole =
        roleFilter === 'ALL' ||
        (emp.role && emp.role.toUpperCase() === roleFilter.toUpperCase());

      return matchText && matchRole;
    });
  }, [employees, searchQuery, roleFilter]);

  // Selection handlers
  const toggleSelectEmployee = (empId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) {
        next.delete(empId);
      } else {
        next.add(empId);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredEmployees.forEach((e) => next.add(e.employeeId));
      return next;
    });
  };

  const handleDeselectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredEmployees.forEach((e) => next.delete(e.employeeId));
      return next;
    });
  };

  const handleApplyTemplate = (tpl) => {
    setTitle(tpl.title);
    setMessage(tpl.message);
    setPlatform(tpl.platform);
    setNotificationType(tpl.type);
    setPreviewTab(tpl.platform === 'WEB' ? 'WEB' : 'MOBILE');
  };

  const handleConfirmDispatch = async () => {
    if (!message.trim()) {
      setToast({ open: true, message: 'Please enter a notification message', severity: 'warning' });
      return;
    }
    if (selectedIds.size === 0) {
      setToast({ open: true, message: 'Please select at least one employee recipient', severity: 'warning' });
      return;
    }

    setDispatching(true);
    setConfirmOpen(false);

    try {
      const payload = {
        title: title.trim() || undefined,
        message: message.trim(),
        platform: platform,
        employeeIds: Array.from(selectedIds),
        type: notificationType,
        context: 'Dev Notification Dispatcher',
        channel: 'AXO',
        sendEmail: sendEmail
      };

      const result = await dispatchNotification(payload);

      setLastDispatched({
        id: result?.notificationId || Date.now(),
        count: selectedIds.size,
        platform: platform,
        title: title || 'Notification',
        timestamp: new Date().toLocaleTimeString()
      });

      setToast({
        open: true,
        message: `Successfully dispatched notification to ${selectedIds.size} employee(s) on ${platform} platform!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Dispatch failed:', err);
      setToast({
        open: true,
        message: 'Failed to dispatch notification. Please check server logs.',
        severity: 'error'
      });
    } finally {
      setDispatching(false);
    }
  };

  const currentPlatformObj = PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, margin: '0 auto' }}>
      {/* 🚀 Header Hero Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
          color: '#F8FAFC',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.25)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(14, 165, 233, 0.25) 0%, transparent 70%)',
            pointerEvents: 'none'
          }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                label="DEV CONSOLE"
                size="small"
                sx={{
                  bgcolor: 'rgba(236, 72, 153, 0.2)',
                  color: '#F472B6',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(244, 114, 182, 0.4)'
                }}
              />
              <Chip
                icon={<IconDeviceAnalytics size={14} color="#38BDF8" />}
                label="Targeted Dispatch API"
                size="small"
                sx={{
                  bgcolor: 'rgba(56, 189, 248, 0.15)',
                  color: '#38BDF8',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(56, 189, 248, 0.3)'
                }}
              />
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.5px', mb: 0.8 }}>
              Push & In-App Notification Dispatcher
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8', maxWidth: 700, lineHeight: 1.6 }}>
              Compose rich updates and target specific employees with mixed roles across <strong>Android Mobile</strong>, <strong>Web Portal</strong>, or <strong>Both</strong>.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchRecipients}
              startIcon={<IconRefresh size={16} />}
              sx={{
                color: '#E2E8F0',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#38BDF8',
                  bgcolor: 'rgba(56, 189, 248, 0.1)'
                }
              }}
            >
              Reload Employees
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Success banner if recently dispatched */}
      {lastDispatched && (
        <Alert
          severity="success"
          onClose={() => setLastDispatched(null)}
          sx={{ mb: 3, borderRadius: 2, fontWeight: 500 }}
        >
          <strong>Notification Sent!</strong> &quot;{lastDispatched.title}&quot; was successfully delivered to {lastDispatched.count} recipient(s) on platform <strong>{lastDispatched.platform}</strong> at {lastDispatched.timestamp}.
        </Alert>
      )}

      {/* Main Grid: Form (Left) & Audience (Right) */}
      <Grid container spacing={3}>
        {/* ========================================================= */}
        {/* LEFT COLUMN: Message & Platform Composer                 */}
        {/* ========================================================= */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={3}>
            {/* Quick Templates */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <IconSparkles size={18} color="#0EA5E9" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
                    Quick Dispatch Templates
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {TEMPLATES.map((tpl, i) => (
                    <Chip
                      key={i}
                      label={tpl.label}
                      onClick={() => handleApplyTemplate(tpl)}
                      clickable
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        py: 0.5,
                        bgcolor: '#F1F5F9',
                        color: '#334155',
                        border: '1px solid #E2E8F0',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#E0F2FE',
                          color: '#0284C7',
                          borderColor: '#38BDF8'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Notification Content Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 2 }}>
                  1. Compose Notification
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Notification Title (Optional)"
                    placeholder="e.g., Mobile App Update Available"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Notification Message"
                    placeholder="Enter the notification text that recipients will read..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    variant="outlined"
                    helperText={`${message.length} characters`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  {/* Notification Type */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', display: 'block', mb: 1, textTransform: 'uppercase' }}>
                      Notification Category
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {['ANNOUNCEMENT', 'UPDATE', 'INFO', 'WARNING', 'SUCCESS'].map((type) => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          onClick={() => setNotificationType(type)}
                          clickable
                          color={notificationType === type ? 'primary' : 'default'}
                          variant={notificationType === type ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Platform Targeting Selector Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                    2. Select Target Platform
                  </Typography>
                  <Chip
                    label={currentPlatformObj.badge}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: currentPlatformObj.bgLight,
                      color: currentPlatformObj.color,
                      border: `1px solid ${currentPlatformObj.color}`
                    }}
                  />
                </Stack>

                <Grid container spacing={1.5}>
                  {PLATFORMS.map((p) => {
                    const isSelected = platform === p.id;
                    const IconComp = p.icon;
                    return (
                      <Grid item xs={12} key={p.id}>
                        <Paper
                          onClick={() => {
                            setPlatform(p.id);
                            setPreviewTab(p.id === 'WEB' ? 'WEB' : 'MOBILE');
                          }}
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            cursor: 'pointer',
                            border: `2px solid ${isSelected ? p.borderActive : '#E2E8F0'}`,
                            bgcolor: isSelected ? p.bgLight : '#FAFAFA',
                            transition: 'all 0.25s ease',
                            boxShadow: isSelected ? `0 4px 14px ${alpha(p.color, 0.25)}` : 'none',
                            '&:hover': {
                              borderColor: p.borderActive,
                              bgcolor: isSelected ? p.bgLight : '#F8FAFC',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isSelected ? p.gradient : '#E2E8F0',
                                color: isSelected ? '#FFFFFF' : '#64748B',
                                flexShrink: 0
                              }}
                            >
                              <IconComp size={24} />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isSelected ? p.color : '#1E293B' }}>
                                  {p.title}
                                </Typography>
                                {isSelected && (
                                  <Chip
                                    label="Selected"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      bgcolor: p.color,
                                      color: '#FFFFFF'
                                    }}
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.8rem', mt: 0.3 }}>
                                {p.description}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        Also dispatch via Company Email (Azure Communication Services)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Sends styled email notification to all selected employees with registered emails.
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            {/* Live Interactive Preview Box */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                    Live Delivery Preview
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={previewTab === 'MOBILE' ? 'contained' : 'outlined'}
                      onClick={() => setPreviewTab('MOBILE')}
                      startIcon={<IconDeviceMobile size={16} />}
                      sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                    >
                      Android Device
                    </Button>
                    <Button
                      size="small"
                      variant={previewTab === 'WEB' ? 'contained' : 'outlined'}
                      onClick={() => setPreviewTab('WEB')}
                      startIcon={<IconBrowser size={16} />}
                      sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                    >
                      Web Drawer
                    </Button>
                  </Stack>
                </Stack>

                {/* Device Preview Content */}
                {previewTab === 'MOBILE' ? (
                  /* Android Notification Mockup */
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                      color: '#FFFFFF',
                      boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {/* Android Status Bar Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, opacity: 0.6 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                        10:45 AM
                      </Typography>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <IconBellRinging size={12} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          AXO Android System
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Android Notification Banner Card */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: '#0EA5E9',
                            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.5)'
                          }}
                        >
                          <IconDeviceMobileMessage size={22} color="#FFFFFF" />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#38BDF8', letterSpacing: 0.5 }}>
                              AXO PEOPLE • JUST NOW
                            </Typography>
                            <Chip
                              label={notificationType}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                bgcolor: 'rgba(14, 165, 233, 0.2)',
                                color: '#38BDF8'
                              }}
                            />
                          </Stack>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', mt: 0.4 }}>
                            {title.trim() || 'AXO People Mobile Notification'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#CBD5E1', fontSize: '0.85rem', mt: 0.4, lineHeight: 1.4 }}>
                            {message.trim() || 'Notification body text will appear here...'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  /* Web Notification Toast Mockup */
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#6366F1',
                          color: '#FFFFFF'
                        }}
                      >
                        <IconBellRinging size={22} />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {title.trim() || 'AXO People Notification'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                            Just now
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                          {message.trim() || 'Notification message text will display here in the Web HRMS feed...'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* ========================================================= */}
        {/* RIGHT COLUMN: Target Audience Employee Selector           */}
        {/* ========================================================= */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Header & Count */}
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                    3. Target Employees
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    Select specific recipients with mixed roles
                  </Typography>
                </Box>
                <Chip
                  icon={<IconUsers size={16} />}
                  label={`${selectedIds.size} of ${employees.length} selected`}
                  color={selectedIds.size > 0 ? 'primary' : 'default'}
                  sx={{ fontWeight: 700, px: 1 }}
                />
              </Stack>

              {/* Search & Role Filters */}
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by employee name, email, department, or office..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size={18} color="#94A3B8" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <IconX size={16} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                {/* Filter Chips by Role */}
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap alignItems="center">
                  <IconFilter size={16} color="#64748B" />
                  {['ALL', 'DEV', 'HR', 'MANAGER', 'EMPLOYEE', 'DIRECTOR'].map((r) => (
                    <Chip
                      key={r}
                      label={r}
                      size="small"
                      clickable
                      onClick={() => setRoleFilter(r)}
                      variant={roleFilter === r ? 'filled' : 'outlined'}
                      color={roleFilter === r ? 'primary' : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  ))}
                </Stack>

                {/* Batch Action Buttons */}
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSelectAllFiltered}
                      startIcon={<IconCheck size={14} />}
                      sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      Select All Filtered ({filteredEmployees.length})
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleDeselectAllFiltered}
                      sx={{ textTransform: 'none', color: '#64748B', fontSize: '0.75rem' }}
                    >
                      Deselect
                    </Button>
                  </Stack>

                  {selectedIds.size > 0 && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setSelectedIds(new Set())}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Clear Selection ({selectedIds.size})
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Employee Scrollable List */}
              <Box
                sx={{
                  flexGrow: 1,
                  maxHeight: 520,
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'rgba(0,0,0,0.15)',
                    borderRadius: 3
                  }
                }}
              >
                {loadingEmployees ? (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" sx={{ color: '#64748B', mt: 1.5 }}>
                      Loading employee directory...
                    </Typography>
                  </Box>
                ) : filteredEmployees.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <IconUsers size={44} color="#CBD5E1" />
                    <Typography variant="subtitle1" sx={{ color: '#64748B', fontWeight: 600, mt: 1 }}>
                      No matching employees found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                      Try adjusting your search query or role filter.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {filteredEmployees.map((emp) => {
                      const isSelected = selectedIds.has(emp.employeeId);
                      const roleStyle = ROLE_COLORS[emp.role] || ROLE_COLORS.EMPLOYEE;
                      const imageUrl = emp.image ? `${IMAGE_BASE_URL}${emp.image}` : null;

                      return (
                        <Grid item xs={12} key={emp.employeeId}>
                          <Paper
                            onClick={() => toggleSelectEmployee(emp.employeeId)}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              cursor: 'pointer',
                              border: `1.5px solid ${isSelected ? '#0EA5E9' : '#E2E8F0'}`,
                              bgcolor: isSelected ? 'rgba(14, 165, 233, 0.05)' : '#FFFFFF',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(14, 165, 233, 0.15)' : 'none',
                              '&:hover': {
                                borderColor: '#0EA5E9',
                                bgcolor: isSelected ? 'rgba(14, 165, 233, 0.08)' : '#F8FAFC',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {/* Checkbox */}
                              <Checkbox
                                checked={isSelected}
                                onChange={() => toggleSelectEmployee(emp.employeeId)}
                                sx={{ p: 0.5 }}
                              />

                              {/* Employee Avatar */}
                              <Avatar
                                src={imageUrl}
                                alt={emp.fullName}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: roleStyle.text,
                                  fontWeight: 700,
                                  fontSize: '0.95rem',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                }}
                              >
                                {emp.fullName
                                  ? emp.fullName
                                      .split(' ')
                                      .map((n) => n[0])
                                      .slice(0, 2)
                                      .join('')
                                      .toUpperCase()
                                  : 'EM'}
                              </Avatar>

                              {/* Details */}
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', noWrap: true }}>
                                    {emp.fullName}
                                  </Typography>
                                  <Chip
                                    label={emp.role || 'EMPLOYEE'}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      bgcolor: roleStyle.bg,
                                      color: roleStyle.text,
                                      border: `1px solid ${roleStyle.border}`
                                    }}
                                  />
                                </Stack>
                                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', noWrap: true }}>
                                  {emp.position || 'Employee'} • {emp.officeName || 'Main Office'}
                                </Typography>
                                {emp.email && (
                                  <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
                                    {emp.email}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>

              <Divider sx={{ my: 2.5 }} />

              {/* Bottom Dispatch Trigger Action */}
              <Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={dispatching || selectedIds.size === 0 || !message.trim()}
                  onClick={() => setConfirmOpen(true)}
                  startIcon={dispatching ? <CircularProgress size={20} color="inherit" /> : <IconSend size={20} />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    background: currentPlatformObj.gradient,
                    boxShadow: `0 4px 16px ${alpha(currentPlatformObj.color, 0.4)}`,
                    '&:hover': {
                      background: currentPlatformObj.gradient,
                      boxShadow: `0 6px 20px ${alpha(currentPlatformObj.color, 0.6)}`
                    }
                  }}
                >
                  {dispatching
                    ? 'Dispatching Notifications...'
                    : `Dispatch to ${selectedIds.size} Employee${selectedIds.size === 1 ? '' : 's'} (${currentPlatformObj.badge})`}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0F172A', pb: 1 }}>
          Confirm Notification Dispatch
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              You are about to broadcast this notification to <strong>{selectedIds.size} selected employee(s)</strong>.
            </Alert>

            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                Platform Targeting
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: currentPlatformObj.color }}>
                {currentPlatformObj.title} ({currentPlatformObj.badge})
              </Typography>

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', mt: 1.5, display: 'block' }}>
                Title
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                {title.trim() || 'No Title (AXO People Default)'}
              </Typography>

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', mt: 1.5, display: 'block' }}>
                Message
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                {message}
              </Typography>

              {sendEmail && (
                <Chip
                  icon={<IconMailForward size={14} />}
                  label="Email notification will also be sent"
                  size="small"
                  color="primary"
                  sx={{ mt: 1.5, fontWeight: 600 }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDispatch}
            disabled={dispatching}
            startIcon={<IconSend size={18} />}
            sx={{
              background: currentPlatformObj.gradient,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2
            }}
          >
            Confirm & Dispatch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
